interface UISession {
  id: string
  flowType: "flow-a" | "flow-b" // flow-a = Upload Room, flow-b = Start From Scratch
  startTime: number
  lastUpdated: number
  data: Record<string, any>
}

class UISessionManager {
  private sessions: Map<string, UISession> = new Map()
  private currentUISessionId: string | null = null

  startSession(flowType: "flow-a" | "flow-b"): string {
    const uiSessionId = `${flowType}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

    const session: UISession = {
      id: uiSessionId,
      flowType,
      startTime: Date.now(),
      lastUpdated: Date.now(),
      data: {},
    }

    this.sessions.set(uiSessionId, session)
    this.currentUISessionId = uiSessionId

    console.log(`🎯 Started new UI session: ${uiSessionId} (${flowType})`)
    return uiSessionId
  }

  updateSession(uiSessionId: string, data: Record<string, any>): void {
    const session = this.sessions.get(uiSessionId)
    if (!session) {
      console.warn(`⚠️ Attempted to update non-existent UI session: ${uiSessionId}`)
      return
    }

    session.data = { ...session.data, ...data }
    session.lastUpdated = Date.now()

    console.log(`📝 Updated UI session ${uiSessionId}:`)
  }

  getSession(uiSessionId: string): UISession | null {
    return this.sessions.get(uiSessionId) || null
  }

  getCurrentSession(): UISession | null {
    if (!this.currentUISessionId) return null
    return this.getSession(this.currentUISessionId)
  }

  getCurrentSessionId(): string | null {
    return this.currentUISessionId
  }

  endSession(uiSessionId: string): void {
    const session = this.sessions.get(uiSessionId)
    if (session) {
      console.log(`🏁 Ended UI session: ${uiSessionId}`)
      this.sessions.delete(uiSessionId)

      if (this.currentUISessionId === uiSessionId) {
        this.currentUISessionId = null
      }
    }
  }

  clearAllSessions(): void {
    const sessionCount = this.sessions.size
    this.sessions.clear()
    this.currentUISessionId = null
    console.log(`🧹 Cleared ${sessionCount} UI sessions`)
  }

  debugState(): void {
    console.log("🔍 UI Session Manager Debug State:")
    console.log("Current UI Session ID:", this.currentUISessionId)
    console.log("Total UI Sessions:", this.sessions.size)
    console.log("All UI Sessions:", Array.from(this.sessions.values()))
  }

  getAllSessions(): UISession[] {
    return Array.from(this.sessions.values())
  }
}

// Singleton instance
let uiSessionManager: UISessionManager | null = null

export function getUISessionManager(): UISessionManager {
  if (!uiSessionManager) {
    uiSessionManager = new UISessionManager()
  }
  return uiSessionManager
}

// === Helper functions for backward-compat (OLD API) ===
export function createUISession(flowType: "flow-a" | "flow-b", data: Record<string, any> = {}): string {
  const uiSessionId = getUISessionManager().startSession(flowType)
  if (Object.keys(data).length) {
    getUISessionManager().updateSession(uiSessionId, data)
  }
  return uiSessionId
}

export function updateUISession(uiSessionId: string, data: Record<string, any>): void {
  getUISessionManager().updateSession(uiSessionId, data)
}

export function getUISession(uiSessionId: string) {
  return getUISessionManager().getSession(uiSessionId)
}

export function endUISession(uiSessionId: string): void {
  getUISessionManager().endSession(uiSessionId)
}

export function getAllUISessions() {
  return getUISessionManager().getAllSessions()
}

export function clearAllUISessions(): void {
  if (uiSessionManager) {
    uiSessionManager.clearAllSessions()
  }
}
